import type { SecurityGroups } from '@communities/content-management/domain';

/**
 * Individual security group row for DataList display
 */
export interface SecurityGroupRow {
  id: string; // Generated unique ID for DataList
  groupName: string; // The actual security group name
  role: 'admin' | 'moderator' | 'user'; // Role category
  roleBadgeText: string; // Display text for role badge
}

/**
 * Transform SecurityGroups structure into individual rows for DataList
 */
export function transformSecurityGroupsToRows(securityGroups: SecurityGroups | null | undefined): SecurityGroupRow[] {
  if (!securityGroups) {
    return [];
  }

  const rows: SecurityGroupRow[] = [];

  // Transform admin groups
  securityGroups.admin.forEach((groupName, index) => {
    rows.push({
      id: `admin-${index}-${groupName}`,
      groupName,
      role: 'admin',
      roleBadgeText: 'Admin',
    });
  });

  // Transform moderator groups
  securityGroups.moderator.forEach((groupName, index) => {
    rows.push({
      id: `moderator-${index}-${groupName}`,
      groupName,
      role: 'moderator',
      roleBadgeText: 'Moderator',
    });
  });

  // Transform user groups
  securityGroups.user.forEach((groupName, index) => {
    rows.push({
      id: `user-${index}-${groupName}`,
      groupName,
      role: 'user',
      roleBadgeText: 'User',
    });
  });

  return rows;
}

/**
 * Transform rows back to SecurityGroups structure
 */
export function transformRowsToSecurityGroups(rows: SecurityGroupRow[]): SecurityGroups {
  const securityGroups: SecurityGroups = {
    admin: [],
    moderator: [],
    user: [],
  };

  rows.forEach(row => {
    securityGroups[row.role].push(row.groupName);
  });

  return securityGroups;
}

/**
 * Check if a security group name already exists in the given role
 */
export function isSecurityGroupDuplicate(securityGroups: SecurityGroups | null | undefined, groupName: string, role: 'admin' | 'moderator' | 'user'): boolean {
  if (!securityGroups) {
    return false;
  }

  const existingGroups = securityGroups[role];

  return existingGroups.includes(groupName);
}

/**
 * Add a new security group to the SecurityGroups structure
 */
export function addSecurityGroup(securityGroups: SecurityGroups | null | undefined, groupName: string, role: 'admin' | 'moderator' | 'user'): SecurityGroups {
  const current = securityGroups || { admin: [], moderator: [], user: [] };

  return {
    ...current,
    [role]: [...current[role], groupName],
  };
}

/**
 * Remove a security group from the SecurityGroups structure
 */
export function removeSecurityGroup(
  securityGroups: SecurityGroups | null | undefined,
  groupName: string,
  role: 'admin' | 'moderator' | 'user'
): SecurityGroups {
  const current = securityGroups || { admin: [], moderator: [], user: [] };

  return {
    ...current,
    [role]: current[role].filter(group => group !== groupName),
  };
}

/**
 * Update a security group in the SecurityGroups structure
 */
export function updateSecurityGroup(
  securityGroups: SecurityGroups | null | undefined,
  changes: { oldGroupName: string; oldRole: 'admin' | 'moderator' | 'user'; newGroupName: string; newRole: 'admin' | 'moderator' | 'user' }
): SecurityGroups {
  const current = securityGroups || { admin: [], moderator: [], user: [] };

  // Remove from old role
  const withoutOld = {
    ...current,
    [changes.oldRole]: current[changes.oldRole].filter(group => group !== changes.oldGroupName),
  };

  // Add to new role
  return {
    ...withoutOld,
    [changes.newRole]: [...withoutOld[changes.newRole], changes.newGroupName],
  };
}
